import { useCallback, useState } from 'react';

import { Translate } from '@spazious/dollhouse-i18n';
import { styled } from '@spazious/dollhouse-theme';
import {
  Backdrop,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogProps,
  Divider,
  Flex,
  Typography,
} from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

const CustomBackgrop = styled(Backdrop)`
  backdrop-filter: blur(10px);
`;

export interface ErrorDialogProps extends Omit<DialogProps, 'onClose'> {
  error: Error;
  onClose?: (evt: {}, reason: 'btnClose' | 'backdropClick' | 'escapeKeyDown') => void;
  title?: string;
  message?: string;
  closeButtonText?: string;
}

export const ErrorDialog: React.FC<ErrorDialogProps> = ({
  error,
  onClose,
  title = 'Ups something went wrong...',
  message,
  closeButtonText = 'Close',
  ...restProps
}) => {
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const handleToggleShowInfo = useCallback(() => {
    setShowMoreInfo((prev) => !prev);
  }, []);

  const handleCloseClick = useEventCallback(() => {
    onClose?.({}, 'btnClose');
  });

  return (
    <Dialog
      backdropComponent={CustomBackgrop}
      maxWidth="md"
      onClose={onClose}
      {...(restProps as any)}
    >
      <DialogHeader>
        <Typography size="small" variant="headline">
          {title}
        </Typography>
      </DialogHeader>
      <Divider />
      <DialogContent>
        <Flex column padding="calc(var(--spacing-padding) * 2) 0 0 0">
          <Typography size="large" variant="body">
            <Translate>We detected the following error in the application:</Translate>
          </Typography>
          {error.message && (
            <Flex padding="16px 0">
              <Typography size="large" variant="body">
                {error.message}
              </Typography>
            </Flex>
          )}

          {message && (
            <Typography size="large" variant="body">
              {message}
            </Typography>
          )}

          {showMoreInfo && (
            <Flex padding="16px 0">
              <code
                style={{
                  marginTop: 'var(--spacing-padding)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  fontSize: '12px',
                }}
              >
                {error?.stack}
              </code>
            </Flex>
          )}
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleToggleShowInfo}>{showMoreInfo ? 'Less' : 'More'} info</Button>
        <Button color="primary" onClick={handleCloseClick}>
          {closeButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
